///
/// EarthBrowser
///
/// Created by Matt Giger on 11/29/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type DatasetSceneObject } from "@/earthbrowser/dataset-distribution/DatasetLayer"
import { TextureSeries } from "@/earthbrowser/datasets/TextureSeries"
import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type RadarVolumeFootprint } from "@/earthbrowser/datasets/RadarVolumeMask"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { Cube } from "@/earthbrowser/entities/geometries/Cube"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import { type Camera } from "@stovokor/camera/Camera"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { clamp, trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { time, timedelta } from "@stovokor/math/time"
import { dot, vec3 } from "@stovokor/math/vector"
import type { NexradStationFeature } from "./NexradTable"
import { RadarAtlasFrame, RadarMaterial } from "./RadarMaterial"
import { RadarRenderer } from "./RadarRenderer"

const cRadarModelAltitude = 1 // 1km above surface
const cRadarFocusDistance = 50 // 50km focus distance
const cRadarFocusMaxZoomRadius = 200 // 200km max zoom
const cHitSphereRadius = 15 // 15km hit sphere for click detection
// The volume box reaches this far from the station horizontally (a square
// in the station's tangent frame), and this high above the site
const cVolumeHalfSizeKm = 200
const cVolumeHeightKm = 20

// Hide the volume when the scene clock is more than this far past the
// station's newest scan. Scans arrive every 4-6 minutes when a station is
// ingesting, so anything beyond this gap means the feed stopped (station
// dropped from the ingest tier set, or a stalled pipeline) and the held
// last frame would masquerade as current weather. Scrubbing the clock back
// into the scan history still renders normally.
const cMaxScanAgeMs = 30 * 60_000

export class RadarStationNode extends GestureNode implements DatasetSceneObject<NexradStationFeature> {
	public readonly focusable: boolean = true
	public readonly id: string

	public feature: NexradStationFeature
	public readonly coordinate: geographic

	private readonly cameraAdapter: EntityCameraAdapter
	private readonly hitSphere = new sphere(cHitSphereRadius)
	private readonly stationPosition: vec3
	private readonly ebContext: EarthBrowserContext
	private boxNode: RadarStationVolume

	// Whether this station may render (and therefore stream) its volume.
	// Set each frame by NexradDatasetLayer, which activates only the few
	// closest stations: atlas frames are ~14MB each with an ~8-frame window
	// per station, so unbounded activation from a wide view exhausts memory
	private volumeActive: boolean = false

	// Newest scan_time in ms, maintained by updateScans() for the staleness
	// gate in render()
	private newestScanMs: number = -Infinity

	// Set by render(): the volume passed every gate this frame and has a
	// scan texture to show, so the flat radar underneath should step back
	// (NexradDatasetLayer publishes these footprints to the RadarVolumeMask)
	public volumeDrawing: boolean = false

	constructor(context: EarthBrowserContext, feature: NexradStationFeature) {
		super(context)
		this.id = feature.station_id
		this.feature = feature
		this.ebContext = context

		// Node origin sits cRadarModelAltitude above the station site. The
		// atlas's layer 0 is at the radar's own elevation (the MRMS 3D cut
		// shifts its MSL levels to above-site, PyART grids relative to the
		// site), so the site elevation must be part
		// of the datum: without it every box grounds at the ellipsoid and
		// neighboring stations at different elevations disagree vertically
		// about shared weather (up to ~2km between mountain-West neighbors)
		const elevationKm = (feature.elevation_m ?? 0) / 1000
		this.coordinate = new geographic(
			feature.lon * trig.rad,
			feature.lat * trig.rad,
			elevationKm + cRadarModelAltitude
		)

		// Build transform for positioning
		const xRot = matrix.axisRotation(vec3.unitX, this.coordinate.latitude + trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, this.coordinate.longitude + trig.pi)
		const modelRotation = yRot.premultiply(xRot)

		this.transform = matrix.translate(this.coordinate.cartesian)
			.premultiply(modelRotation)
		this.stationPosition = this.coordinate.cartesian

		// Create the radar volume visualization
		this.boxNode = new RadarStationVolume(context)
		this.boxNode.textureSeries.debugLabel = `nexrad:${feature.station_id}`
		// Scale to cover ±200km horizontally (400km total) and 20km vertically.
		// In this tangent frame -Y points away from the surface and the node
		// origin sits cRadarModelAltitude (1km) above the station site, so
		// translate the box down (node +Y) by 9km: it then spans node y +1
		// (the site elevation, the data grid's z=0) to -19 (20km above site)
		// instead of straddling the surface with half the volume underground.
		this.boxNode.transform = matrix.translate(0, cRadarModelAltitude - cVolumeHeightKm / 2, 0)
			.premultiply(matrix.scale(2 * cVolumeHalfSizeKm, cVolumeHeightKm, 2 * cVolumeHalfSizeKm))
		this.addNode(this.boxNode)

		// Create camera adapter for focus control
		this.cameraAdapter = new EntityCameraAdapter(context, this, cRadarFocusDistance, cRadarFocusMaxZoomRadius)

		this.updateScans()
	}

	/**
	 * Update the feature data for this scene object
	 * Called by DatasetLayer when the dataset updates
	 */
	public updateFeature(feature: NexradStationFeature): void {
		this.feature = feature
		this.updateScans()
	}

	/**
	 * Free all GPU atlas textures (~14MB each) while keeping scan metadata.
	 * Called when the nexrad layer is hidden so disabling the dataset
	 * actually reclaims memory; atlases reload when re-shown.
	 */
	public purgeTextures(): void {
		this.boxNode.textureSeries.purgeAll()
	}

	/**
	 * Distance from a world-space position (typically the camera) to the
	 * station, in km. Used by NexradDatasetLayer to rank stations for
	 * volume activation.
	 */
	public distanceTo(position: vec3): number {
		return this.stationPosition.subtract(position).length
	}

	/** Whether this station's volume is currently allowed to render/stream */
	public get isVolumeActive(): boolean {
		return this.volumeActive
	}

	/** Ground footprint of the volume box, for the flat radar hand-off */
	public get volumeFootprint(): RadarVolumeFootprint {
		return {
			id: this.id,
			lon: this.coordinate.longitude,
			lat: this.coordinate.latitude,
			halfSizeKm: cVolumeHalfSizeKm,
		}
	}

	/**
	 * Activate or deactivate this station's volume. Deactivation purges the
	 * atlas textures immediately (they reload quickly from the image cache
	 * when the station reactivates).
	 */
	public setVolumeActive(active: boolean): void {
		if (this.volumeActive === active) return
		this.volumeActive = active
		if (!active) {
			// render() may not run again while the layer is hidden, so the
			// drawing flag has to drop here or the footprint would stick
			this.volumeDrawing = false
			this.boxNode.textureSeries.purgeAll()
		}
	}

	/**
	 * Update the texture series with the current scans
	 */
	private updateScans(): void {
		this.newestScanMs = -Infinity
		const frames = this.feature.scans.map(scan => {
			const scanTime = new time(scan.scan_time)
			const scanMs = scanTime.date.getTime()
			if (scanMs > this.newestScanMs) {
				this.newestScanMs = scanMs
			}
			return new RadarAtlasFrame(this.context, scan.atlas_url, scanTime, timedelta.minutes(5))
		})
		this.boxNode.textureSeries.setFrames(frames)
	}

	// ========== Rendering ==========

	/**
	 * Skip stations that aren't activated by the layer (only the closest few
	 * stations may stream their volumes) or are occluded by the earth. The
	 * engine only frustum-culls, so stations on the far side of the globe
	 * are still "visible" (inside the view cone, just behind the planet)
	 * and would stream multi-MB atlas frames, starving the bandwidth of the
	 * station actually being viewed.
	 */
	override render(state: RenderState): RenderActionSet[] {
		this.volumeDrawing = false
		if (!this.volumeActive) {
			return []
		}

		// Staleness gate: past the newest scan the texture series would hold
		// its last frame forever, so stop rendering (and streaming) once the
		// clock is beyond cMaxScanAgeMs of it. Historical scrubbing still
		// works because the gate is relative to the scene clock, not wall time
		const clockMs = this.ebContext.clock.clockTime.date.getTime()
		if (clockMs - this.newestScanMs > cMaxScanAgeMs) {
			return []
		}

		const cameraDistance = state.camera.position.length
		const cosAngle = dot(state.camera.position, this.stationPosition)
			/ (cameraDistance * this.stationPosition.length)
		const angle = Math.acos(clamp(cosAngle, -1, 1))

		// Horizon arc from the camera plus the arc from the top of the radar
		// volume (20km), plus slack for the volume's 200km horizontal reach.
		// wgs.b (polar radius) keeps the occluder conservative so stations
		// never pop out while still legitimately visible.
		const horizon = Math.acos(Math.min(1, wgs.b / cameraDistance))
			+ Math.acos(Math.min(1, wgs.b / (wgs.a + cVolumeHeightKm)))
			+ cVolumeHalfSizeKm / wgs.a

		if(angle > horizon) {
			return []
		}
		// Only a volume with a scan texture on screen displaces the flat
		// radar; until the atlas lands the mosaic keeps carrying the echoes
		const series = this.boxNode.textureSeries
		this.volumeDrawing = series.texture0 !== this.ebContext.blankTexture && series.texture0.available
		return super.render(state)
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)

		// Push to browser history for back/forward navigation
		pushTargetToHistory('radar', this.feature.station_id)
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		// Delegate camera and compass commands to the adapter
		return this.cameraAdapter.handleCommand(command, data)
	}

	// ========== Gesture Handling ==========

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const dist = this.hitSphere.intersect(state.ray)
		if (dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}
}

// Atlas frames are 1920x1920 RGBA (~14MB of GPU memory each), and a station
// can carry hundreds of 5-minute scans, so purge frames outside the preload
// window. A couple of frames are preloaded behind the current one so
// backward scrubbing has textures ready.
class RadarVolumeTextureSeries extends TextureSeries<RadarAtlasFrame> {
	constructor(context: EarthBrowserContext) {
		super(context)
		this.purgeUnused = true
		this.preloadCount = 6
		this.backPreloadCount = 2
	}
}

class RadarStationVolume extends NodeBase {
	public material: RadarMaterial
	public textureSeries: TextureSeries<RadarAtlasFrame>
	public available = true
	public transparency = true

	constructor(context: EarthBrowserContext) {
		super()
		const geometry = new Cube(context)
		this.textureSeries = new RadarVolumeTextureSeries(context)

		// threshold=5 dBZ matches the 2D overlay palette minimum.
		// density is the Beer-Lambert extinction coefficient per box unit of
		// travel (the box is 400 km across, 20 km tall, so 1 unit of vertical
		// travel is the whole depth), independent of the march step.
		// density=80 takes a 3 km deep layer of light rain (20 dBZ) to ~94%
		// opacity seen from above and a 2 km core of 45 dBZ to ~99%. That
		// matches what the volume looked like before back-face culling was
		// restored: it was drawn twice per pixel, squaring its transmittance,
		// and the look was tuned against that. density=40 gives the
		// single-draw calibration (~75% / ~92%, near the mosaic's 0.78 echo
		// opacity) if a lighter volume is preferred.
		// stepTexels=0.5 marches half a data texel per sample along the ray's
		// dominant axis (the old fixed step was ~0.2 texel vertically, ~0.4
		// horizontally) with no start jitter; indistinguishable from the old
		// look. Coarser steps would need the jitter back, and the jitter is
		// what shows (speckle / hatching), so this is the floor. maxSteps=500
		// is a safety cap (the longest ray, a horizontal diagonal, is ~385
		// steps)
		this.material = new RadarMaterial(this.textureSeries, 5, 80.0, 0.5, 500, 20)
		this.renderer = new RadarRenderer(context, geometry, this.material)
	}
}
